import { createAsyncThunk } from '@reduxjs/toolkit';
import { makeApiCall } from '../../utils/makeApiCall';
import { serializeError } from '../../utils/errorHandling';
import { axiosClient } from '../../api/axiosClient';
import { getChangeRequestGroups } from '../../utils/groupMenuBarItems';
import { Result, ChangeRequestGroup } from '../../interfaces/changeRequest';
import { ChangeRequestListingScope } from '../../enums/changeRequest';
import { isTeamUser } from '../../services/subscription';

export const fetchAllChangeRequests = createAsyncThunk(
  'changeRequests/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchChangeRequestsApiCall(ChangeRequestListingScope.ALL);
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

export const fetchMyChangeRequests = createAsyncThunk(
  'changeRequests/fetchMy',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchChangeRequestsApiCall(ChangeRequestListingScope.MY);
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

export const fetchChangeRequests = createAsyncThunk(
  'changeRequests/fetchAllAndMy',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(fetchMyChangeRequests()).unwrap();
      if (isTeamUser()) {
        await dispatch(fetchAllChangeRequests()).unwrap();
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchChangeRequestsApiCall = async (
  scope: ChangeRequestListingScope
) => {
  const response = await makeApiCall({
    url: '/cr',
    method: 'GET',
    params: { scope },
  });
  const sorted = [...response.data].sort(
    (a, b) =>
      new Date(b.last_active_at).getTime() -
      new Date(a.last_active_at).getTime()
  );
  const groupedData = getChangeRequestGroups(sorted);

  return {
    data: sorted,
    groupedData,
  };
};

type ChangeRequestsPayload = {
  data: Result[];
  groupedData: ChangeRequestGroup[];
};

export const deleteChangeRequests = createAsyncThunk<
  Record<ChangeRequestListingScope, ChangeRequestsPayload>,
  { ids: string[] },
  { rejectValue: string }
>('changeRequests/delete', async ({ ids }, { rejectWithValue }) => {
  try {
    const res = await axiosClient({
      url: `/cr/batch-delete`,
      method: 'POST',
      data: { ids },
    });

    const responseData = Array.isArray(res.data) ? res.data[0] : res.data;
    if (!responseData?.success) {
      return rejectWithValue(
        responseData?.error || 'Unable to delete change requests'
      );
    }

    if (isTeamUser()) {
      const changeRequestLists = await Promise.all([
        fetchChangeRequestsApiCall(ChangeRequestListingScope.MY),
        fetchChangeRequestsApiCall(ChangeRequestListingScope.ALL),
      ]);
      return {
        [ChangeRequestListingScope.MY]: changeRequestLists[0],
        [ChangeRequestListingScope.ALL]: changeRequestLists[1],
      };
    } else {
      return {
        [ChangeRequestListingScope.MY]: await fetchChangeRequestsApiCall(
          ChangeRequestListingScope.MY
        ),
        [ChangeRequestListingScope.ALL]: { data: [], groupedData: [] },
      };
    }
  } catch (error) {
    return rejectWithValue((error as Error).message || 'Unexpected error');
  }
});
